import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { adminAuthAPI } from '../lib/adminApi';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard, Users, Key, Shield, LogOut, Phone, Menu, X, Settings, PhoneOutgoing, PieChart, Volume2, Mail, MessageSquareText, FileText, TrendingUp
} from 'lucide-react';

const navItems = [
  { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Overview', end: true },
  { path: '/admin/dashboard/users', icon: Users, label: 'Users' },
  { path: '/admin/dashboard/api-keys', icon: Key, label: 'API Keys' },
  { path: '/admin/dashboard/demo-calls', icon: PhoneOutgoing, label: 'Demo Calls' },
  { path: '/admin/dashboard/showcases', icon: Volume2, label: 'Showcases' },
  { path: '/admin/dashboard/contact-submissions', icon: Mail, label: 'Contact Forms' },
  { path: '/admin/dashboard/chatbot-leads', icon: MessageSquareText, label: 'Chatbot Leads' },
  { path: '/admin/dashboard/blog', icon: FileText, label: 'Blog' },
  { path: '/admin/dashboard/seo', icon: TrendingUp, label: 'Advanced SEO' },
  { path: '/admin/dashboard/investors', icon: PieChart, label: 'Investors' },
  { path: '/admin/dashboard/settings', icon: Settings, label: 'Settings' },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('dotcall_admin_token');
    if (!token) { navigate('/admin'); return; }
    adminAuthAPI.me()
      .then(res => { setAdmin(res.data); setLoading(false); })
      .catch(() => { localStorage.removeItem('dotcall_admin_token'); navigate('/admin'); });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('dotcall_admin_token');
    localStorage.removeItem('dotcall_admin');
    navigate('/admin');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#020617]">
      <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  const SidebarContent = () => (
    <>
      <div className="p-5 border-b border-white/5">
        <div className="flex items-center gap-3">
          <img src="https://i.ibb.co/ZRpFcYDK/oie-s-XZ9-BUTHM5-Vz.png" alt="DotCall" className="h-8 w-auto" />
          <span className="text-xs text-red-400 font-medium">SUPER ADMIN</span>
        </div>
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1" data-testid="admin-sidebar-nav">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive
                  ? 'bg-red-600/15 text-red-300 border-r-2 border-red-500'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`
            }
            data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-4 h-4 flex-shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-red-600/20 flex items-center justify-center text-red-300 text-xs font-semibold">
            SA
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate">{admin?.name || 'Admin'}</div>
            <div className="text-xs text-slate-500 truncate">{admin?.email}</div>
          </div>
        </div>
        <Button variant="ghost" onClick={handleLogout} data-testid="admin-logout-button"
          className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10 text-sm h-9">
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#020617' }} data-testid="admin-layout">
      <aside className="hidden lg:flex lg:flex-col w-60 border-r border-white/5 bg-[#0B1221]/80 backdrop-blur-xl flex-shrink-0">
        <SidebarContent />
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-60 h-full flex flex-col bg-[#0B1221] border-r border-white/5">
            <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-slate-400"><X className="w-5 h-5" /></button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="lg:hidden flex items-center gap-3 p-4 border-b border-white/5 bg-[#0B1221]/80 backdrop-blur-xl">
          <button onClick={() => setSidebarOpen(true)}><Menu className="w-5 h-5 text-slate-300" /></button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-400" />
            <span className="font-bold font-['Outfit'] text-white">Admin Panel</span>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6 lg:p-8" data-testid="admin-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
